import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
// Import Redux Actions
import moment from "moment";
import CustomSelect from "../../../../components/Select/Select";
import {
  createDiscount,
  deleteDiscount,
  getDiscounts,
  updateDiscount,
} from "../../../../redux/reducers/discountSlice";
import { petOwnershipTypesForDrop } from "../../../../util/dropList";
import { tblGrayBack } from "../../../../util/object";
import generatePass from "../../../../util/randomPassword";

function Discount() {
  const dispatch = useDispatch();
  const { discounts, loading } = useSelector((state) => state.discount); // From Redux

  const initialFormState = {
    discountMethod: "discount code",
    discountCode: "",
    generateRandomCode: false,
    startDate: "",
    endDate: "",
    discountValue: "",
    discountType: "Percentage",
    appliesTo: "All Products/Services",
    purchaseType: "One-time",
    searchCategories: "",
    selectedCategories: [],
    customerTypes: [],
    minimumPurchase: "No minimum amount",
    minAmount: "",
    minQuantity: "",
    eligibility: "All customers",
    specificCustomers: "",
    limitUsageTotal: false,
    maxTotalUses: "",
    limitUsagePerCustomer: false,
    maxPerCustomerUses: "",
    cannotBeCombined: false,
  };

  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [openEditDialog, setOpenEditDialog] = React.useState(false);
  const [editIndex, setEditIndex] = React.useState(null);
  const [form, setForm] = React.useState(initialFormState);
  const [editForm, setEditForm] = React.useState(initialFormState);
  const [editMode, setEditMode] = React.useState(false);
  const [delModVisible, setDelModVisible] = React.useState(false);
  const [deleteIndex, setDeleteIndex] = React.useState(null);

  useEffect(() => {
    dispatch(getDiscounts()); // Fetch data on mount
  }, [dispatch]);

  const resetForm = () => {
    setForm(initialFormState);
    setEditForm(initialFormState);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    resetForm();
    setEditMode(false);
  };

  const handleSave = () => {
    const payload = {
      ...form,
      searchCategories: undefined,
      specificCustomers:
        form.eligibility === "Specific Customers"
          ? [form.specificCustomers]
          : [],
      startDate: !isNaN(Date.parse(form?.startDate))
        ? moment(new Date(form?.startDate)).format("YYYY-MM-DD")
        : null,
      endDate: !isNaN(Date.parse(form?.endDate))
        ? moment(new Date(form?.endDate)).format("YYYY-MM-DD")
        : null,
    };
    if (payload?.eligibility === "New customers") {
      payload.specificCustomers = ["newCustomers"];
    }
    if (payload?.eligibility === "Customer Type") {
      payload.specificCustomers = form.customerTypes;
    }
    if (editMode && form.billId) {
      dispatch(updateDiscount({ id: form.billId, ...payload }));
    } else {
      dispatch(createDiscount(payload));
    }

    handleCloseDrawer();
  };

  const handleEdit = (index) => {
    setForm(discounts[index]);
    setEditMode(true);
    setEditIndex(index);
    setOpenDrawer(true);
  };

  const handleUpdate = () => {
    dispatch(updateDiscount({ id: editForm.billId, ...editForm }));
    setOpenEditDialog(false);
    setEditIndex(null);
    resetForm();
  };

  const handleDelete = (index) => {
    setDeleteIndex(index);
    setDelModVisible(true);
  };

  const handleDelModClose = () => {
    setDeleteIndex(null);
    setDelModVisible(false);
  };

  const handleDelConfirm = () => {
    if (deleteIndex !== null) {
      const billId = discounts[deleteIndex].billId;
      dispatch(deleteDiscount(billId));
    }
    handleDelModClose();
  };

  const handleFormChange = (field, value) => {
    if (field === "generateRandomCode") {
      if (value) {
        setForm((prev) => ({
          ...prev,
          [field]: value,
          discountCode: generatePass(8), // Set random code
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          [field]: value,
          discountCode: "", // Clear if unchecked
        }));
      }
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const removeCategory = (categoryToRemove, isEdit = false) => {
    const targetForm = isEdit ? editForm : form;
    const targetSetter = isEdit ? handleEditFormChange : handleFormChange;
    targetSetter(
      "selectedCategories",
      targetForm.selectedCategories.filter((cat) => cat !== categoryToRemove)
    );
  };

  const addCategory = (isEdit = false) => {
    const targetForm = isEdit ? editForm : form;
    const targetSetter = isEdit ? handleEditFormChange : handleFormChange;
    if (
      targetForm.searchCategories &&
      !targetForm.selectedCategories.includes(targetForm.searchCategories)
    ) {
      targetSetter("selectedCategories", [
        ...targetForm.selectedCategories,
        targetForm.searchCategories,
      ]);
      targetSetter("searchCategories", "");
    }
  };

  const renderFormFields = (formData, handleChange, isEdit = false) => (
    <>
      <FormControl component="fieldset" margin="normal">
        <Box>
          <Typography fontWeight="bold">Discount Method</Typography>
        </Box>
        <RadioGroup
          row
          value={formData.discountMethod}
          onChange={(e) => handleChange("discountMethod", e.target.value)}
        >
          <FormControlLabel
            value="Discount code"
            control={<Radio />}
            label="Discount code"
          />
          {/* <FormControlLabel
            value="Automatic discount"
            control={<Radio />}
            label="Automatic discount"
          /> */}
        </RadioGroup>
      </FormControl>

      {formData.discountMethod === "Discount code" && (
        <>
          <TextField
            fullWidth
            label="Discount Code"
            value={formData.discountCode}
            onChange={(e) => handleChange("discountCode", e.target.value)}
            margin="normal"
            disabled={formData.generateRandomCode}
            InputLabelProps={{
              sx: {
                position: "absolute",
                top: "50%",
                left: "14px",
                transform: "translateY(-50%)",
                transition: "all 0.2s ease-out",
                "&.Mui-focused, &.MuiFormLabel-filled": {
                  top: "0px",
                  transform: "translateY(-50%) scale(0.75)",
                  transformOrigin: "top left",
                },
              },
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.generateRandomCode}
                onChange={(e) =>
                  handleChange("generateRandomCode", e.target.checked)
                }
              />
            }
            label="Generate random code"
          />
        </>
      )}

      <Grid container spacing={2}>
        <Box my={2} mx={2}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <CustomTextField
                name="startDate"
                value={formData.startDate}
                label="Start Date"
                fullWidth
                handleChange={(e) => handleChange("startDate", e.target.value)}
                mobileDateWithBatch
                markDateList={[]}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomTextField
                name="endDate"
                value={formData.endDate}
                label="End Date"
                fullWidth
                handleChange={(e) => handleChange("endDate", e.target.value)}
                mobileDateWithBatch
                markDateList={[]}
              />
            </Grid>
          </Grid>
        </Box>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            select
            fullWidth
            label="Discount Type"
            value={formData.discountType}
            onChange={(e) => handleChange("discountType", e.target.value)}
            margin="normal"
          >
            {["Percentage", "Fixed Amount"].map((opt, i) => (
              <MenuItem key={i} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={formData.discountType === "Fixed Amount" ? "₹" : "%"}
            value={formData.discountValue}
            onChange={(e) => handleChange("discountValue", e.target.value)}
            margin="normal"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            select
            fullWidth
            label="Applies to"
            value={formData.appliesTo}
            onChange={(e) => handleChange("appliesTo", e.target.value)}
            margin="normal"
          >
            {[
              "All Products/Services",
              // "Specific Categories",
              // "Specific Collection",
            ].map((opt, i) => (
              <MenuItem key={i} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            select
            fullWidth
            label="Purchase Type"
            value={formData.purchaseType}
            onChange={(e) => handleChange("purchaseType", e.target.value)}
            margin="normal"
          >
            {["One-time", "Recurring"].map((opt, i) => (
              <MenuItem key={i} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {formData.appliesTo === "Specific Collection" && (
        <Box mt={2}>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="Search Collection"
                value={formData.searchCategories}
                onChange={(e) =>
                  handleChange("searchCategories", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={4}>
              <Button
                variant="contained"
                onClick={() => addCategory(isEdit)}
                sx={{ height: "40px" }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
          <Box mt={1}>
            {formData.selectedCategories.map((category, idx) => (
              <Chip
                key={idx}
                label={category}
                onDelete={() => removeCategory(category, isEdit)}
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        </Box>
      )}
      {formData.appliesTo === "Specific Categories" && (
        <Box mt={2}>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="Search Collection"
                value={formData.searchCategories}
                onChange={(e) =>
                  handleChange("searchCategories", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={4}>
              <Button
                variant="contained"
                onClick={() => addCategory(isEdit)}
                sx={{ height: "40px" }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
          <Box mt={1}>
            {formData.selectedCategories.map((category, idx) => (
              <Chip
                key={idx}
                label={category}
                onDelete={() => removeCategory(category, isEdit)}
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        </Box>
      )}

      <FormControl component="fieldset" margin="normal">
        {/* <FormLabel component="legend">Minimum purchase requirements</FormLabel> */}
        <Box>
          <Typography fontWeight="bold">
            Minimum purchase requirements
          </Typography>
        </Box>

        <RadioGroup
          value={formData.minimumPurchase}
          onChange={(e) => handleChange("minimumPurchase", e.target.value)}
        >
          <FormControlLabel
            value="No minimum amount"
            control={<Radio />}
            label="No minimum amount"
          />
          <FormControlLabel
            value="Minimum purchase amount"
            control={<Radio />}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <span>Minimum purchase amount</span>
                {formData.minimumPurchase === "Minimum purchase amount" && (
                  <TextField
                    size="small"
                    value={formData.minAmount}
                    onChange={(e) => handleChange("minAmount", e.target.value)}
                    sx={{ width: "100px" }}
                  />
                )}
              </Box>
            }
          />
          <FormControlLabel
            value="Minimum quantity"
            control={<Radio />}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <span>Minimum quantity</span>
                {formData.minimumPurchase === "Minimum quantity" && (
                  <TextField
                    size="small"
                    value={formData.minQuantity}
                    onChange={(e) =>
                      handleChange("minQuantity", e.target.value)
                    }
                    sx={{ width: "100px" }}
                  />
                )}
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>

      <FormControl component="fieldset" margin="normal">
        {/* <FormLabel component="legend">Eligibility</FormLabel> */}
        <Box>
          <Typography fontWeight="bold">Eligibility</Typography>
        </Box>

        <RadioGroup
          value={formData.eligibility}
          onChange={(e) => handleChange("eligibility", e.target.value)}
        >
          <FormControlLabel
            value="All customers"
            control={<Radio />}
            label="All customers"
          />
          <FormControlLabel
            value="New customers"
            control={<Radio />}
            label="New customers"
          />
          {/* <FormControlLabel
            value="Specific Customers"
            control={<Radio />}
            label="Specific Customers"
          /> */}
          <FormControlLabel
            value="Customer Type"
            control={<Radio />}
            label="Customer Type"
          />
        </RadioGroup>
      </FormControl>
      {form.eligibility === "Customer Type" && (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <CustomSelect
              list={petOwnershipTypesForDrop}
              value={formData?.customerTypes}
              handleChange={(e) => {
                handleChange("customerTypes", e);
              }}
              reason
              label="Customer Type"
            />
          </Grid>
        </Grid>
      )}

      {formData.eligibility === "Specific Customers" && (
        <Grid container spacing={1}>
          <Grid item xs={8}>
            <TextField
              fullWidth
              label="Search Customers"
              value={formData.specificCustomers}
              onChange={(e) =>
                handleChange("specificCustomers", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={4}>
            <Button variant="contained" sx={{ height: "56px" }}>
              Search
            </Button>
          </Grid>
        </Grid>
      )}

      <Typography variant="subtitle1" sx={{ mt: 2 }}>
        Usage Restrictions
      </Typography>

      <FormControlLabel
        control={<Checkbox />}
        label={
          <Box display="flex" alignItems="center" gap={1}>
            <span>
              Limit number of times this discount can be used in total
            </span>
            {formData.limitUsageTotal && (
              <TextField
                size="small"
                value={formData.maxTotalUses}
                onChange={(e) => handleChange("maxTotalUses", e.target.value)}
                sx={{ width: "80px" }}
              />
            )}
          </Box>
        }
        checked={formData.limitUsageTotal}
        onChange={(e) => handleChange("limitUsageTotal", e.target.checked)}
      />

      <FormControlLabel
        control={<Checkbox />}
        label={
          <Box display="flex" alignItems="center" gap={1}>
            <span>Limit number of times per customer</span>
            {formData.limitUsagePerCustomer && (
              <TextField
                size="small"
                value={formData.maxPerCustomerUses}
                onChange={(e) =>
                  handleChange("maxPerCustomerUses", e.target.value)
                }
                sx={{ width: "80px" }}
              />
            )}
          </Box>
        }
        checked={formData.limitUsagePerCustomer}
        onChange={(e) =>
          handleChange("limitUsagePerCustomer", e.target.checked)
        }
      />

      <FormControlLabel
        control={<Checkbox />}
        label="Cannot be combined with other discounts"
        checked={formData.cannotBeCombined}
        onChange={(e) => handleChange("cannotBeCombined", e.target.checked)}
      />
    </>
  );

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5"> Discounts</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenDrawer(true)}
        >
          Add Discount
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              {[
                "S.No",
                "Discount Type",
                "Discount Code",
                "Validity",
                "Created by",
                "Created Date",
                "Action",
              ].map((li, i) => (
                <TableCell
                  style={tblGrayBack}
                  className="txt-semi-bold fs12"
                  key={i + li}
                >
                  {li}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : discounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              discounts.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.discountMethod}</TableCell>
                  <TableCell>{row.discountCode}</TableCell>
                  <TableCell>
                    Start: {new Date(row.startDate).toLocaleDateString()}
                    <br />
                    End: {new Date(row.endDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{row.userName || "-"}</TableCell>
                  <TableCell>
                    {new Date(row.createdDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleEdit(index)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleDelete(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Drawer */}
      <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer}>
        <Box p={3} width={450} sx={{ height: "100vh", overflowY: "auto" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" color="primary">
              {editMode ? "Edit Discount" : "Add Discount"}
            </Typography>{" "}
            <IconButton onClick={handleCloseDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>
          {renderFormFields(form, handleFormChange)}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3 }}
            onClick={handleSave}
          >
            {editMode ? "Update" : "Save"}
          </Button>{" "}
        </Box>
      </Drawer>

      {/* Edit Dialog */}
      <CustomModal
        open={delModVisible}
        onClose={handleDelModClose}
        header="Delete"
        modal
        modalWidth={30}
      >
        <Typography className="flex-center txt-regular fs14">
          Are you sure you want to delete this record?
        </Typography>

        <div className="flex-center mb10 mt15">
          <div>
            <CustomButton text="Cancel" onClick={handleDelModClose} />
          </div>
          <div className="ml10">
            <CustomButton redBtn text="Confirm" onClick={handleDelConfirm} />
          </div>
        </div>
      </CustomModal>
    </Box>
  );
}

export default Discount;
